import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEvent } from "../../../context/EventContext";
import Loader from "../../Loader/Loader";
import { useState } from "react";

// The Location component renders the location form for creating or updating a location.
const Location = ({ onNext }) => {
  const {
    LocationformData,
    setLocationFormData,
    handleLocationChange,
    setLoading,
    loading,
    locations,
    locationAdded,
    setLocationAdded,
    setLocations,
    editIndex,
    setEditIndex,
    mainEventID,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);

  // Handles the "Add Space" button click by setting the isFormVisible state to true,
  // which displays the location form.
  const handleAddSpace = () => {
    setIsFormVisible(true);
  };

  // Handles the "Add Location" button click by resetting the form state and showing the form.
  const handleLocationAdd = () => {
    setLocationFormData({
      space_name: "",
      space_code: "",
      space_type: "",
      space_capacity: "",
      space_address: "",
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setEditIndex(null); // Set editIndex to null when adding a new location
  };

  // Handles the "Edit" button click for a location.
  const handleEdit = (index) => {
    setLocationFormData(locations[index]);
    setIsFormVisible(true);
    setEditIndex(index);
  };

  // Handles the "Save" button click in the location form by sending a POST request to the server.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        const response = await eventsAPI.post("/organizer/location", {
            ...LocationformData, // Spread LocationformData as an object
            mainEventID, // Add mainEventID as part of the payload
          });
      if (response.status === 200) {
        toast.success("Location added successfully!");
        setLoading(false);
        setIsFormVisible(false);
        setLocationAdded(true);

        if (editIndex !== null) {
          const updatedLocations = locations.map((location, index) =>
            index === editIndex ? response.data : location
          );
          setLocations(updatedLocations);
        } else {
          setLocations([...locations, response.data]);
        }
      }
    } catch (error) {
      console.log("Error creating Location:", error);
      setLoading(false);
    }
  };

  //   * Handles the "Update" button click in the location form by sending a PUT request to the server.
  //   * If successful, it updates the local state with the updated location data and shows a success message.
  //   * If the request fails, it shows an error message.
  //   * It also updates the local state by updating the title, start, and end dates of the location in the form data.
  //   * This function is used to update the location details in the database when the user edits a location.
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateLocation",
        LocationformData
      );

      if (response.status === 200) {
        toast.success("Location updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated location data
        const updatedLocations = locations.map((location, index) =>
          index === editIndex ? response.data : location
        );
        setLocations(updatedLocations);

        // Check if there are any subEvents left
        if (updatedLocations.length === 0) {
          setLocationAdded(false);
        }
      }
    } catch (error) {
      console.log("Error updating Location:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a location by sending a DELETE request to the server.
  //   * If successful, it removes the location from the local state and shows a success message.
  //   * If the request fails, it shows an error message
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/organizer/location/${id}`); // Pass the location's id

      if (response.status === 200) {
        toast.success("Location deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted location from the local state
        const updatedLocations = locations.filter(
          (location) => location._id !== id
        );
        setLocations(updatedLocations);
      }
    } catch (error) {
      console.log("Error deleting Location:", error);
      setLoading(false);
    }
  };

  return (
    <div className="location-section">
      {/* Show "Add Space" button only if no location has been added yet and the form is not visible */}
      {!locationAdded && !isFormVisible && locations.length === 0 && (
        <button className="add-space" onClick={handleAddSpace}>
          Add Space
        </button>
      )}

      {/* Show form only when isFormVisible is true */}
      {isFormVisible && (
        <form>
          <h2>Location Details</h2>
          <div className="space-details">
            <label htmlFor="space-name">Space Name</label>
            <input
              name="space_name"
              type="text"
              placeholder="Enter the name of your space"
              value={LocationformData.space_name}
              onChange={handleLocationChange}
              required
            />
            <label htmlFor="space-code">Space Code</label>
            <input
              name="space_code"
              type="text"
              placeholder="Enter the code of your space"
              value={LocationformData.space_code}
              onChange={handleLocationChange}
              required
            />
            <label htmlFor="space-type">Space Type</label>
            <select
              name="space_type"
              value={LocationformData.space_type}
              onChange={handleLocationChange}
              required
            >
              <option value="select">Select</option>
              <option value="hall">Hall</option>
              <option value="meeting room">Meeting Room</option>
              <option value="open space">Open Space</option>
              <option value="auditorium">Auditorium</option>
              <option value="other">Other</option>
            </select>
            <label htmlFor="space-capacity">Space Capacity</label>
            <input
              name="space_capacity"
              type="number"
              placeholder="Enter the capacity of your hall"
              value={LocationformData.space_capacity}
              onChange={handleLocationChange}
              required
            />
            <label htmlFor="space-address">Floor Address</label>
            <input
              name="space_address"
              type="text"
              placeholder="Enter the floor address of your space"
              value={LocationformData.space_address}
              onChange={handleLocationChange}
              required
            />
          </div>

          {/* Show the "Save" button if adding a new location, and "Update" button if editing */}
          {editIndex === null ? (
            <button className="create-button" onClick={handleSave}>
              {loading ? <Loader /> : "SAVE"}
            </button>
          ) : (
            <>
              <button className="create-button" onClick={handleUpdate}>
                {loading ? <Loader /> : "UPDATE"}
              </button>
              <button
                className="create-button"
                onClick={(e) => handleDelete(e, LocationformData._id)}
              >
                {loading ? <Loader /> : "DELETE"}
              </button>
            </>
          )}
        </form>
      )}

      {/* Show preview section if location has been added and form is not visible */}
      {!isFormVisible && locationAdded && (
        <>
          {locations.map((location, index) => (
            <div className="saved-details" key={index}>
              <p>
                <span>Space Name: </span> {location.space_name}
              </p>
              <p>
                <span>Space Code: </span> {location.space_code}
              </p>
              <button className="add-space" onClick={() => handleEdit(index)}>
                Edit
              </button>
            </div>
          ))}
          <div className="buttons-controller">
            <button className="add-space" onClick={onNext}>
              Next
            </button>
            <button className="add-space" onClick={handleLocationAdd}>
              Add Another Space
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Location;
